/**
 * 构建期 API base 输入解析 —— 同源契约（specs/common/deployment/15 §4、16 P3）。
 *
 * 【为什么需要解析而不是直接透传？】
 * 应用代码普遍以 `VITE_*_API_BASE_URL || "http://localhost:50051"` 的方式读取
 * 构建输入：空字符串会被 `||` 吃掉而错误回退到 localhost。而部署轨道的
 * same-origin 形态恰恰以空字符串表达"API 经边缘 Worker / nginx 同源反代，
 * base 即页面 origin"——两种语义必须区分：
 *
 * - `""`（显式空串）：同源构建（Cloudflare / 裸 nginx 面）→ 页面 origin。
 *   下游 `new URL("/oauth2/authorize", base)` 需要绝对 base，空串会抛错，
 *   因此这里必须解析成 `window.location.origin`；
 * - 非空字符串：显式绝对 base（本地开发直连服务端口）；
 * - `undefined`：构建未注入该变量 → 回退 devFallback（本地开发默认值），
 *   保持本地开发体验不变。
 *
 * @example
 * // 同源构建（VITE_IDAAS_API_BASE_URL=""）→ "https://admin.adamzfc.top"
 * resolveApiBase(runtimeEnv?.VITE_IDAAS_API_BASE_URL, "http://localhost:50051")
 */

/**
 * 解析构建期 API base 输入。
 *
 * @param value - 构建注入的 `VITE_*_API_BASE_URL` 原始值（可能为 undefined）
 * @param devFallback - 变量未注入时的本地开发回退（必须是绝对 URL）
 * @returns 实际使用的绝对 base；同源构建时为页面 origin
 */
export const resolveApiBase = (value: string | undefined, devFallback: string): string => {
  if (value === "") return window.location.origin;
  return value ?? devFallback;
};

/**
 * 构建期（Node 侧，vite.config）CSP connect-src origin 派生。
 *
 * 与 resolveApiBase 的 "" 语义一致：同源构建不需要任何显式 origin
 * （`connect-src 'self'` 已覆盖页面 origin），返回 null 供调用方过滤；
 * 其余输入解析为 origin 字符串。
 *
 * @param value - 构建注入的 `VITE_*_API_BASE_URL` 原始值
 * @param devFallback - 变量未注入时的本地开发回退（必须是绝对 URL）
 * @returns origin 字符串；同源构建（""）时为 null
 */
export const buildCspOrigin = (value: string | undefined, devFallback: string): string | null =>
  value === "" ? null : new URL(value ?? devFallback).origin;

/**
 * 可选构建输入的同源解析：`""` → 页面 origin，其余原样透传（含 undefined）。
 *
 * 供"变量未注入时走降级分支"的调用方使用（如 admin 未配置即匿名壳）：
 * resolveApiBase 的 devFallback 语义会改变这类调用的分支行为。
 *
 * @param value - 构建注入的可选 `VITE_*_BASE_URL` 原始值
 * @returns 同源构建时为页面 origin；其余原样返回（undefined 保持 undefined）
 */
export const resolveOptionalApiBase = (value: string | undefined): string | undefined =>
  value === "" ? window.location.origin : value;
